import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import FlexboxContainer from 'components/UI/FlexboxContainer';
import { useAPI } from 'lib/API';
import { useCurrencies } from 'hooks/useCurrencies';
import { useCountries } from 'hooks/useCountries';
import LoadingIndicator from 'components/UI/LoadingIndicator';
import PromptInput from './PromptInput';
import ProcessingState from './ProcessingState';
import GeneratedVariantsTable from './GeneratedVariantsTable';
import { Sorting } from 'common/ProductDetailsV2';
import { renderToast, toast } from 'components/UI/ToastNotification';
import { post } from 'lib/API';
import FeatureFlag from 'data/FeatureFlag';
import Vendor from 'data/Vendor';
import Variant from 'data/Variant';
import Product, * as Prod from 'data/Product';
import ConfirmModal from 'components/UI/Modal';
import SmartVariantsActionComponent from './SmartVariantsActionComponent';
import {
  ACTION_TYPE,
  CONFIRMATION_ACTION,
  STAGE,
} from 'constants/aiVariantImport';
import { ROUTES, TEXT_LABELS } from 'constants/productDetails';
import {
  getEmptyInventoryErrors,
  VariantError,
  VariantErrorType,
} from 'data/ProductValidation';
import { unique } from 'lib/filters';
import TableV2 from 'view/TableV2';
import { Size } from 'lib/styles';
import AttributesEditor from 'common/AttributesEditor';
import Attribute from 'data/Attribute';
import { PRODUCT_STATUS } from 'constants/product';
import TokenLimitExhausted from './TokenLimitExhausted';
import { fetchVendorMetaData } from 'requests/meta';
import { STORAGE_KEYS } from 'constants/general';
import { save } from 'lib/SessionStorage';
import { useAuth } from 'lib/Auth';
import { useVendorBusinessUnitPersistence } from 'hooks/useVendorBusinessUnitPersistence';
import { getMidnightUTC, getResetTimeInfo } from 'utils/smartVariants';
import { navigate } from 'hooks/useHookRouterCompat';
import {
  getTotalProductVariantPrices,
  syncVariantCommissionFields,
} from 'utils/variant';
import Pagination from 'components/UI/Pagination';
import { extendedPaginationSelectOptions } from 'constants/paginationOptions';

const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background-color: white;
  box-sizing: border-box;
`;

const MainContent = styled.div`
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  box-sizing: border-box;
`;

const InnerContainer = styled.div`
  display: inline-block;
  // margin: 6rem 0px 0px;
  width: 100%;
  padding: ${Size.medium};
`;

interface SelectedRows {
  delete: any[];
  update: any[];
  create: any[];
}

interface CustomPagination {
  itemsPerPage: number;
  setItemsPerPage: (value: number) => void;
  currentPage: number;
  setCurrentPage: (value: number) => void;
  totalPages: number | undefined;
  baseRoute: string;
  shouldRedirect: boolean;
}

interface Pagination {
  actual: number;
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalRecords: number;
}

const SmartVariantsView = ({
  productId,
  selectedVendor,
  featureFlags,
  productVariants,
  product,
  loading,
  loadProducts,
  variantErrors,
  onActionButtonsStateChange,
  loadProductVariants,
  update,
  vendorAttributes,
  reloadVendorAttributes,
  disabled,
  user,
  customPagination,
  isAIEnabled
}: {
  productId: any;
  selectedVendor: Vendor;
  featureFlags: FeatureFlag;
  productVariants: Variant[];
  product: Product;
  loading: boolean;
  loadProducts: () => void;
  variantErrors?: VariantError[];
  onActionButtonsStateChange?: (hasPendingActions: boolean) => void;
  loadProductVariants: (resetPagination?: boolean) => void;
  update: (product: Product) => void;
  vendorAttributes: Attribute[];
  reloadVendorAttributes: (value: boolean) => void;
  disabled?: boolean;
  user?: any;
  customPagination: CustomPagination;
  isAIEnabled: any;
}) => {
  const [stage, setStage] = useState<string>(STAGE.PROMPT);
  const [prompt, setPrompt] = useState('');
  const [aiResponse, setAIResponse] = useState<any[]>([]);
  const [recentPrompts, setRecentPrompts] = useState<string[]>([]);
  const [showRecentPrompts, setShowRecentPrompts] = useState(false);
  const [isResLoading, setIsResLoading] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [irrelevantQuery, setIrrelevantQuery] = useState<any>(null);
  const [pendingBothOperations, setPendingBothOperations] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState<VariantError[]>([]);
  const [editingAttributes, setEditingAttributes] = useState(false);
  const [latestVendorDetails, setLatestVendorDetails] = useState<Vendor | null>(null);
  const [sortByHeader, setSortByHeader] = useState<Sorting | undefined>(
    undefined
  );
  const [selectedRows, setSelectedRows] = useState<SelectedRows>({
    delete: [],
    update: [],
    create: [],
  });
  const [confirmationAction, setConfirmationAction] = useState<
    | typeof CONFIRMATION_ACTION.APPROVE
    | typeof CONFIRMATION_ACTION.REJECT
    | null
  >(null);
  const [selectedVariants, setSelectedVariants] = useState<number[]>([]);
  const [allVariantsSelection, setAllVariantsSelection] = React.useState(false);

  const { currencies } = useCurrencies();
  const { countries } = useCountries();
  const { persistVendorSelection } = useVendorBusinessUnitPersistence();
  const { token } = useAuth();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef<boolean>(true);

  let newVariants: Variant[] = productVariants.map((x) => {
    const variant = product.variants.find((y) => x.id === y.id);
    return {
      ...x,
      rentalPriceCA: variant?.rentalPriceCA || [],
      rentalPriceUS: variant?.rentalPriceUS || [],
      rent: variant?.rent,
      purchase: variant?.purchase,
    };
  });

  // const validationErrors = validateProduct(product, this.props.role);
  const InventoryError = getEmptyInventoryErrors(product);

  let tableRows = newVariants.map((variant) => {
    return {
      variant,
      errorInventoryTypes: InventoryError.filter((error) =>
        error.variants.map((vari) => vari.id).includes(variant.id)
      )
        .map((error) => error.type)
        .filter(unique),
      errorTypes: variantErrors!
        .filter((error) =>
          error.variants.map((vari) => vari.id).includes(variant.id)
        )
        .map((error) => error.type)
        .filter(unique),
      focused: true,
    };
  });

  // Track if action buttons are visible and no action has been performed
  // Action buttons are visible when aiResponse has data and stage is SUCCESS
  // No action has been performed when user hasn't approved/rejected (not loading and no confirmation action)
  const hasPendingActions =
    aiResponse &&
    aiResponse.length > 0 &&
    stage === STAGE.SUCCESS &&
    !isResLoading &&
    !showConfirmationModal &&
    confirmationAction === null;

  // Notify parent component about action buttons state
  useEffect(() => {
    if (onActionButtonsStateChange) {
      onActionButtonsStateChange(hasPendingActions);
    }
  }, [hasPendingActions, onActionButtonsStateChange]);

  useEffect(() => {
    if ((product && (product?.status === TEXT_LABELS.PUBLISHED || product?.status === TEXT_LABELS.READY)) || !isAIEnabled) {
      navigate(ROUTES.PRODUCTS); // Redirect to product listing page
    }
  }, [product]);

  const resetAllStates = () => {
    setStage(STAGE.PROMPT);
    setPrompt('');
    setAIResponse([]);
    setSelectedRows({
      delete: [],
      update: [],
      create: [],
    });
    setShowConfirmationModal(false);
    setConfirmationAction(null);
    setIrrelevantQuery(null);
  };

  const getRecentPromptsReq = useAPI({
    deferFn: async ([productId, vendorId]: any[], { token }: any) => {
      return post('v2/variants/getRecentPrompts', token, {
        productId,
        vendorId,
      });
    },
    onResolve: (res) => {
      setRecentPrompts(res.prompts);
    },
    onReject: (err: any) => {
      renderToast(toast.TYPE.ERROR, 'Failed to fetch recent prompts.');
    },
  });

  const fetchVendorData = useCallback(async () => {
    if (!selectedVendor?.id || !token) return;
    
    try {
      const result = await fetchVendorMetaData(token);

      if (result?.meta) {
        const { vendors } = result?.meta;
        // Save all metadata to session storage
        const metaDataToSave = {
          [STORAGE_KEYS.VENDORS]: vendors,
        };

        Object.entries(metaDataToSave).forEach(([key, value]) => {
          if (value) save(key, value);
        });

        const selectedVendorDetails = vendors.find((vendor: Vendor) => vendor.id === selectedVendor.id);
        if (selectedVendorDetails) {
          persistVendorSelection(selectedVendorDetails);
          setLatestVendorDetails(selectedVendorDetails);
        }
      }
    } catch (error: any) {
      renderToast(toast.TYPE.ERROR, error.message);
    }
  }, [selectedVendor?.id, token, persistVendorSelection]);

  useEffect(() => {
    if (productId && selectedVendor?.id) {
      getRecentPromptsReq.run(productId, selectedVendor.id);
    }
  }, [productId, selectedVendor?.id]);

  // Fetch vendor data on mount and when selectedVendor changes
  useEffect(() => {
    if (selectedVendor?.id && token) {
      fetchVendorData();
    }
  }, [selectedVendor?.id, token]);

  // Auto-refresh vendor data at midnight UTC to check if token limit has reset
  useEffect(() => {
    isMountedRef.current = true;

    const scheduleNextMidnightCheck = () => {
      if (!isMountedRef.current) {
        return;
      }

      const delayMs = getMidnightUTC();
      timeoutRef.current = setTimeout(() => {
        // Only proceed if component is still mounted
        if (!isMountedRef.current) {
          return;
        }

        // Refetch vendor data to check if limit has reset
        fetchVendorData();
        // Schedule next check for the following midnight
        scheduleNextMidnightCheck();
      }, delayMs);
    };

    scheduleNextMidnightCheck();

    return () => {
      isMountedRef.current = false;
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [fetchVendorData]);

  const generateVariantsReq = useAPI({
    deferFn: async ([promptText, prodId]: any[], { token }: any) => {

      const encoded = window.btoa(encodeURIComponent(promptText));
      console.log('[SmartVariants] Original prompt:', promptText);
      console.log('[SmartVariants] Encoded prompt (sent to API):', encoded);

      let reqBody:any = {
        promptInput: encoded,
        productId: prodId,
        vendorId: selectedVendor.id,
      }
      
      if(selectedVariants.length > 0) {
        reqBody.skus = selectedVariants
        .map(id => productVariants.find(v => v.id === id))
        .filter((v): v is Variant => v !== undefined).map(v => v?.sku);
      }

      return post('v2/variants/generateVariants', token, reqBody);
    },
    onResolve: (res) => {
      setStage(STAGE.SUCCESS);
      fetchVendorData()
      getRecentPromptsReq.run(productId, selectedVendor.id);

      if (res.noVariantsExtracted) {
        renderToast(toast.TYPE.WARNING, res.message);
        return;
      }

      if (res && res.data.length > 0) {
        // If there are more than 8 variants, show a success message
        if (product?.variants && product?.variants.length > 8) {
          //
          renderToast(
            toast.TYPE.SUCCESS,
            'Variants generated successfully. Please scroll below to see the generated variants.'
          );
        }
        res.data.forEach((item: any, index: number) => {
          if (item[ACTION_TYPE.UPDATE]) {
            setAIResponse((prev: any) => [
              ...prev,
              {
                id: index,
                actionType: ACTION_TYPE.UPDATE,
                data: item[ACTION_TYPE.UPDATE]?.variants,
              },
            ]);
          }
          if (item[ACTION_TYPE.CREATE]) {
            setAIResponse((prev: any) => [
              ...prev,
              {
                id: index,
                actionType: ACTION_TYPE.CREATE,
                data: item[ACTION_TYPE.CREATE]?.variants,
              },
            ]);
          }
          if (item[ACTION_TYPE.DELETE]) {
            setAIResponse((prev: any) => [
              ...prev,
              {
                id: index,
                actionType: ACTION_TYPE.DELETE,
                data: item[ACTION_TYPE.DELETE]?.variants,
              },
            ]);
          }
        });
      }

      if(res && res.errors && res.errors.length > 0) {
        setIrrelevantQuery(res.errors);
      }
    },
    onReject: (error: any) => {
      fetchVendorData()

      setStage(STAGE.SUCCESS);
      const errorMessage =
        error.response?.data?.error ||
        error.message ||
        error.error ||
        'Failed to generate variants';

      renderToast(toast.TYPE.ERROR, errorMessage);
    },
  });

  const createVariantsReq = useAPI({
    deferFn: async ([variantsData, prodId]: any[], { token }: any) => {
      return post('v2/variants/updateVariants', token, {
        variants: variantsData,
        productId: prodId,
      });
    },
    onResolve: () => {
      renderToast(toast.TYPE.SUCCESS, 'Variants created successfully.');
      if (!pendingBothOperations) {
        setIsResLoading(false);
        resetAllStates();
        loadProducts();
        loadProductVariants();
        getRecentPromptsReq.run(productId, selectedVendor.id);
        fetchVendorData()


      }
    },
    onReject: (err: any) => {
      fetchVendorData()
      renderToast(toast.TYPE.ERROR, 'Failed to create the variants.');
      if (!pendingBothOperations) {
        setIsResLoading(false);
      }
    },
  });

  const saveDraftReq = useAPI({
    deferFn: async (
      [productData, vendor, currencyList]: any[],
      { token }: any
    ) => {
      return Prod.saveLocalDraft(productData, vendor, token, currencyList);
    },
    onResolve: () => {
      renderToast(toast.TYPE.SUCCESS, 'Variants updated successfully.');
      if (!pendingBothOperations) {
        setIsResLoading(false);
        resetAllStates();
        loadProducts();
        loadProductVariants();
        getRecentPromptsReq.run(productId, selectedVendor.id);
        fetchVendorData()


      }
    },
    onReject: (err: any) => {
      fetchVendorData()
      renderToast(toast.TYPE.ERROR, 'Failed to update the variants.');
      if (!pendingBothOperations) {
        setIsResLoading(false);
      }
    },
  });

  //Check here reload issue
  // Handle completion when both operations are pending
  useEffect(() => {
    if (pendingBothOperations) {
      const createDone =
        (createVariantsReq.data || createVariantsReq.error) &&
        !createVariantsReq.isLoading;
      const updateDone =
        (saveDraftReq.data || saveDraftReq.error) && !saveDraftReq.isLoading;

      if (createDone && updateDone) {
        setIsResLoading(false);
        resetAllStates();
        loadProducts();
        loadProductVariants();
        setPendingBothOperations(false);
        getRecentPromptsReq.run(productId, selectedVendor.id);
        fetchVendorData();
      }
    }
  }, [
    pendingBothOperations,
    createVariantsReq.data,
    createVariantsReq.error,
    createVariantsReq.isLoading,
    saveDraftReq.data,
    saveDraftReq.error,
    saveDraftReq.isLoading,
    loadProducts,
    loadProductVariants,
    getRecentPromptsReq,
    productId,
    selectedVendor?.id,
  ]);


  const handlePromptSubmit = async () => {
    if (!prompt.trim()) {
      renderToast(toast.TYPE.ERROR, 'Please enter a prompt');
      return;
    }

    // Add prompt to recent prompts array
    const trimmedPrompt = prompt.trim();
    setIrrelevantQuery(null);
    setStage(STAGE.PROCESSING);
    setAIResponse([]);
    generateVariantsReq.run(trimmedPrompt, productId);
  };

  if (loading || isResLoading) {
    return (
      <PageContainer>
        <MainContent>
          <LoadingIndicator />
        </MainContent>
      </PageContainer>
    );
  }

  if (!product) {
    return (
      <PageContainer>
        <MainContent>
          <div>Product not found</div>
        </MainContent>
      </PageContainer>
    );
  }

  const syncVariantCommission = (variant: Variant, originalVariant?: Variant | null): Variant =>
    syncVariantCommissionFields(variant, countries, originalVariant);

  const processAIResponses = async () => {
    let result: any = {};
    let newProduct: Product | null = null;
    let needsUpdate = false;

    for (const res of aiResponse) {
      if (res.actionType === ACTION_TYPE.CREATE) {
        const { create } = selectedRows;
        const createdVariants = create.filter((variant => variant.sku)).map((variant) => {
          const synced = syncVariantCommission(variant);
          return {
            ...synced,
            id: null,
            productVariantPrices: getTotalProductVariantPrices(synced, currencies),
          };
        });
        result.create = { variants: createdVariants };
        continue;
      }

      // Only create newProduct when UPDATE or DELETE is needed
      if (!newProduct) {
        newProduct = { ...product };
      }

      if (res.actionType === ACTION_TYPE.UPDATE) {
        for (const variant of selectedRows.update) {
          const { id } = variant;
          let updatedVariant = syncVariantCommission(
            variant,
            product.variants?.find((v: Variant) => v.id === id)
          );
          let updatedVariantIndex = newProduct.variants.findIndex(
            (v: Variant) => v.id === id
          );
          updatedVariant.dirty = true;
          newProduct.variants[updatedVariantIndex] = updatedVariant;
        }
        needsUpdate = true;
      }

      if (res.actionType === ACTION_TYPE.DELETE) {
        if (selectedRows.delete.length > 0) {
          newProduct.variantsToDelete = selectedRows.delete;
        }
        needsUpdate = true;
      }
    }

    if (needsUpdate && newProduct) {
      result.update = newProduct;
    }

    return result;
  };

const handleApprove = async () => {
    const result = await processAIResponses();
    if (result) {
      const { create, update } = result;
      setIsResLoading(true);
      if (create && update) {
        // Both operations present - wait for both to complete
        setPendingBothOperations(true);
        createVariantsReq.run(create, productId);
        saveDraftReq.run(update, selectedVendor, currencies);
      } else if (create) {
        // Only create operation
        createVariantsReq.run(create, productId);
      } else if (update) {
        // Only update operation
        saveDraftReq.run(update, selectedVendor, currencies);
      }
    }
  };

  const handleRejectClick = () => {
    setConfirmationAction(CONFIRMATION_ACTION.REJECT);
    setShowConfirmationModal(true);
  };

  const handleApproveClick = () => {
    // Check if selectedRows has any entries; throw error if all keys are empty arrays
    const hasSelectedEntries = Object.values(selectedRows).some(
      (arr) => Array.isArray(arr) && arr.length > 0
    );
    if (!hasSelectedEntries) {
      renderToast(toast.TYPE.ERROR, 'No variants selected for processing.');
      return;
    }
    setConfirmationAction(CONFIRMATION_ACTION.APPROVE);
    setShowConfirmationModal(true);
  };

  const handleConfirmationConfirm = async () => {
    if (confirmationAction === CONFIRMATION_ACTION.REJECT) {
      resetAllStates();
    } else if (confirmationAction === CONFIRMATION_ACTION.APPROVE) {
      setShowConfirmationModal(false);
      setConfirmationAction(null);
      setIrrelevantQuery(null);
      await handleApprove();
    }
  };

  const handleConfirmationCancel = () => {
    setShowConfirmationModal(false);
    setConfirmationAction(null);
  };

  const onAttributesEdit = () => {
    setEditingAttributes(true);
  };

  const isDraftStatus = product?.status === PRODUCT_STATUS.DRAFT;


  const confirmationModal = (
    <ConfirmModal
      showModal={showConfirmationModal}
      onClose={handleConfirmationCancel}
      onClick={handleConfirmationConfirm}
      onGhostButtonClick={handleConfirmationCancel}
      title={TEXT_LABELS.CONFIRM_ACTION}
      ghostButtonText={TEXT_LABELS.CANCEL}
      primaryButtonText={TEXT_LABELS.CONFIRM}
      width="440px"
      theme="accent"
    >
      <>
        {confirmationAction === CONFIRMATION_ACTION.REJECT
          ? TEXT_LABELS.REJECT_CONFIRMATION
          : TEXT_LABELS.APPROVE_CONFIRMATION}
      </>
    </ConfirmModal>
  );

  const isErrorColumn = (key: string, sku: string) => {
    const selectedIssueTemp = selectedIssue.filter((issue) => {
      const existIssueInVariant = issue.variants.find(
        (variant) => variant.sku === sku
      );
      return existIssueInVariant && sku ? true : false;
    });

    const selectedIssueTypes = selectedIssueTemp.map(
      (selectedIssueObj) => selectedIssueObj.type
    );

    const selectedIssueKeys = selectedIssueTemp
      .filter(
        (item) =>
          item.type === VariantErrorType.MissingValue ||
          item.type === VariantErrorType.EmptyAttributeValue ||
          item.type === VariantErrorType.RentRangeSkipped ||
          item.type === VariantErrorType.RentRangeOrder ||
          item.type === VariantErrorType.RequiredRentalRange
      )
      .map((selectedIssueObj) => selectedIssueObj?.key);

    if (selectedIssueTypes.length) {
      if (
        selectedIssueTypes.includes(VariantErrorType.SKUValidation) ||
        selectedIssueTypes.includes(VariantErrorType.DuplicateUniqueValue) ||
        selectedIssueTypes.includes(VariantErrorType.InvalidSKUValue) ||
        selectedIssueTypes.includes(VariantErrorType.InvalidSKULength) ||
        selectedIssueTypes.includes(
          VariantErrorType.InvalidMaterialNumberLength
        )
      ) {
        if (key === TEXT_LABELS.SKU_SMALL) return true;
      }
      if (
        selectedIssueTypes.includes(
          VariantErrorType.MaterialNumberValidation
        ) ||
        selectedIssueTypes.includes(VariantErrorType.InvalidMaterialNumber) ||
        selectedIssueTypes.includes(VariantErrorType.DuplicateMaterialNumber) ||
        selectedIssueTypes.includes(VariantErrorType.EmptyMaterialNumber)
      ) {
        if (key === TEXT_LABELS.MATERIAL_NUMBER) return true;
      }

      if (
        selectedIssueTypes.includes(VariantErrorType.EmptyConsignmentType) &&
        [TEXT_LABELS.PURCHASE, TEXT_LABELS.RENT].includes(key)
      ) {
        return true;
      }

      if (selectedIssueTypes.includes(VariantErrorType.RequiredRentalRange)) {
        return selectedIssueTemp.some((x) => x.key?.includes(key)) || false;
      }

      if (selectedIssueTypes.includes(VariantErrorType.MissingValue)) {
        return selectedIssueKeys.includes(key);
      }

      if (selectedIssueTypes.includes(VariantErrorType.EmptyAttributeValue)) {
        return selectedIssueKeys.includes(key);
      }

      if (selectedIssueTypes.includes(VariantErrorType.DuplicateVariant)) {
        return product.attributes
          .map((attribute) => attribute.name)
          .includes(key);
      }

      if (selectedIssueTypes.includes(VariantErrorType.RentRangeOrder)) {
        return selectedIssueTemp.some((x) => x.key === key) || false;
      }

      if (selectedIssueTypes.includes(VariantErrorType.RentRangeSkipped)) {
        return selectedIssueTemp.some((x) => x.key === key) || false;
      }
    }

    return false;
  };


 const onCellEdit = (
    newAttributeValue: string | number | boolean,
    variant: Variant,
    attributeKey: string,
    keyId: number | null = null,
    extras: any = {}
  ) => {
    if (attributeKey === TEXT_LABELS.CHECK_BOX) {
      const alreadySelected = selectedVariants.find(
        (id) => variant.id === id
      );
      if (alreadySelected) {
        const filteredSelectedVariants = selectedVariants.filter(
          (id) => alreadySelected !== id
        );
        setSelectedVariants(filteredSelectedVariants);
      } else {
        variant.id &&
          setSelectedVariants([
            ...selectedVariants,
            variant.id,
          ]);
      }
    }
  };

  const handleSelectUnselectAllVariants = (isChecked: boolean) => {
    const allVariantsIds = isChecked
      ? productVariants.map((variant) => variant.id)
      : [];
    setSelectedVariants(allVariantsIds as number[]);
  };

  const handleRemoveVariant = (variantId: number) => {
    setSelectedVariants(prev => prev.filter(id => id !== variantId));
  };

  return (
    <>
      <FlexboxContainer margin="0 -45px 0 -75px">
        <SmartVariantsActionComponent
          onApprove={handleApproveClick}
          onReject={handleRejectClick}
          isResLoading={isResLoading}
          showModal={showRecentPrompts}
          onClose={() => setShowRecentPrompts(false)}
          recentPrompts={recentPrompts}
          onSelectPrompt={(prompt) => setPrompt(prompt)}
          handleModalOpen={() => setShowRecentPrompts(true)}
          stage={stage}
          showActionButton={aiResponse && aiResponse.length > 0}
          variantErrors={variantErrors}
          productVariants={productVariants}
          product={product}
          selectedIssue={selectedIssue}
          setSelectedIssue={setSelectedIssue}
          onAttributesEdit={onAttributesEdit}
          disabled={disabled}
          isDraftStatus={isDraftStatus}
        />
      </FlexboxContainer>

      <AttributesEditor
        showModal={editingAttributes}
        onClose={() => setEditingAttributes(false)}
        product={product}
        update={update}
        selectedVendor={selectedVendor}
        vendorAttributes={vendorAttributes.map((el) => {
          return { id: el.id, code: el.code, name: el.name };
        })}
        reloadVendorAttributes={reloadVendorAttributes}
        featureFlags={featureFlags}
      />

      {confirmationModal}

      <PromptInput
        prompt={prompt}
        setPrompt={setPrompt}
        examplePrompts={[]}
        onSubmit={handlePromptSubmit}
        stage={stage}
        disabled={product && (product?.status === TEXT_LABELS.PUBLISHED || product?.status === TEXT_LABELS.READY) ? true : latestVendorDetails?.tokenLimitExceeded ?? selectedVendor?.tokenLimitExceeded ?? false}
        selectedVariants={selectedVariants}
        productVariants={productVariants}
        onRemoveVariant={handleRemoveVariant}
        variantErrors={irrelevantQuery}
      />

      {(latestVendorDetails?.tokenLimitExceeded ?? selectedVendor?.tokenLimitExceeded) && (
        <TokenLimitExhausted
          resetTime={getResetTimeInfo().resetTime}
        />
      )}

      {stage === STAGE.PROCESSING && <ProcessingState />}

      {product?.variants && product?.variants.length > 0 && (
        <InnerContainer>
          <TableV2
            key="available-variants"
            classifications={product.attributes.filter(
              (attribute) => !attribute.isSelectable
            )}
            focusedMode={selectedIssue.length > 0}
            isErrorColumn={isErrorColumn}
            onAttributeEdit={() => {}}
            onRowDelete={null as any}
            onCellEdit={onCellEdit}
            rows={tableRows}
            suggestionValuesList={[]}
            attributes={product.attributes.filter(
              (attribute) => attribute.isSelectable
            )}
            clearSuggestions={() => {}}
            originalId={product.originalId}
            product={product}
            disabled={true}
            publishedSKUList={[]}
            featureFlags={featureFlags}
            currencies={currencies}
            selectedVendor={selectedVendor}
            stockFlag={false}
            selectedVariants={selectedVariants}
            setSortByHeader={setSortByHeader}
            sortByHeader={sortByHeader}
            selectUnselectAllVariants={handleSelectUnselectAllVariants}
            allVariantsSelection={allVariantsSelection}
            setAllVariantsSelection={setAllVariantsSelection}
            type={''}
            filteredWarehouseIds={[]}            
            columnSettings={[]}
            smartVariantMode={true}
            maxHeight="40vh"
          />

          {productVariants && productVariants.length > 0 && (
            <div style={{ margin: "10px -5px", width: "100%" }} >
              <Pagination
                {...customPagination}
                selectorOptions={extendedPaginationSelectOptions}
              />
            </div>
          )}
        </InnerContainer>
      )}

      {(stage === STAGE.REVIEW || stage === STAGE.SUCCESS) && (
        <>
          {aiResponse && aiResponse.length > 0 && (
            <>
              {aiResponse?.map((res, index) => {
                const { actionType, data } = res;
                return (
                  <FlexboxContainer
                    margin="24px 0px"
                    width="100%"
                    key={`container-${actionType}-${index}`}
                  >
                      <GeneratedVariantsTable
                        key={`${actionType}-${index}`}
                        variants={data}
                        actionType={actionType}
                        product={product}
                        featureFlags={featureFlags}
                        currencies={currencies}
                        countries={countries}
                        selectedVendor={selectedVendor}
                        user={user}
                        isSelectable={true}
                        setSelectedRows={setSelectedRows}
                        selectedRows={selectedRows}
                        vendorAttributes={vendorAttributes}
                        isEditable={
                          actionType === ACTION_TYPE.DELETE ? false : true
                        }
                      />
                  </FlexboxContainer>
                );
              })}
            </>
          )}
        </>
      )}
    </>
  );
};

export default SmartVariantsView;
